import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PageBanner from "../../components/PageBanner";
import { useIsMobile, MoreToggle } from "../../components/ShowMore";
import { getNcc } from "../../services/data.service";
import assetUrl from "../../services/assetUrl";
import "../home/homeRedesign.css"; // shared primitives: .btn-ed, .eyebrow-ed, .heading-ed
import "./nccNew.css";

const EASE = [0.23, 1, 0.32, 1];
const containerV = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const itemV = {
  hidden: { opacity: 0, transform: "translateY(20px)" },
  show: { opacity: 1, transform: "translateY(0px)", transition: { duration: 0.45, ease: EASE } },
};

const Rise = ({ children, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, transform: "translateY(26px)" }}
    whileInView={{ opacity: 1, transform: "translateY(0px)" }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, ease: EASE }}
  >
    {children}
  </motion.div>
);

const IconDoc = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
);
const IconArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);
const IconShield = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l7 3v6c0 5-3.5 9-7 11-3.5-2-7-6-7-11V5l7-3z" /></svg>
);
const IconFlag = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3v18M5 4h9l-1.5 3L14 10H5" /></svg>
);
const IconStar = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l2.8 5.7L21 9.6l-4.5 4.3 1.1 6.1L12 17l-5.6 3 1.1-6.1L3 9.6l6.2-.9L12 3z" /></svg>
);

const NCC = () => {
  const [ncc, setNcc] = React.useState(null);
  const isMobile = useIsMobile();
  const [showAll, setShowAll] = React.useState(false);
  const DOCS_INITIAL = 4;

  useEffect(() => { 
    window.scrollTo(0, 0); 
    const fetchData = async () => {
      try {
        const res = await getNcc();
        if (res && res.success) {
          // All NCC media lives in S3 — assetUrl maps /images/.., /view/..,
          // ncet.co.in and \uploads\.. onto the right host (http URLs pass through).
          const data = res.data;
          if (data.about?.image) data.about.image = assetUrl(data.about.image);
          if (data.head?.image) data.head.image = assetUrl(data.head.image);
          if (data.documents?.items) {
             data.documents.items = data.documents.items.map(item => ({
               ...item,
               url: assetUrl(item.url),
             }));
          }
          setNcc(data);
        }
      } catch (err) {
        console.error("Failed to load NCC data:", err);
      }
    };
    fetchData();
  }, []);

  if (!ncc) {
    return <div style={{ padding: '100px', textAlign: 'center' }}>Loading...</div>;
  }

  const { hero, about, head, documents, quote } = ncc;
  const docsShown = isMobile && !showAll ? documents.items.slice(0, DOCS_INITIAL) : documents.items;

  const titleParts = (hero.title || "").trim().split(" ");
  const titleLast = titleParts.pop();

  return (
    <div className="ncx-page">
      <Header parentMenu="outreach" topbarEnable="enable" />

      <PageBanner
        title={hero.title}
        eyebrow={hero.eyebrow}
        subtitle={hero.subtitle}
        image={hero.bannerImage ? assetUrl(hero.bannerImage) : undefined}
      >
        <div style={{ display: "flex", gap: "1rem", marginTop: "2.5rem", flexWrap: "wrap" }}>
          <a href="#ncc-docs" className="hero-btn hero-btn--primary">Explore Documents</a>
          <a href="#ncc-officer" className="hero-btn hero-btn--ghost">Meet NCC Officer</a>
        </div>
      </PageBanner>

      <section className="ncx-stats">
        <div className="container">
          <div className="ncx-stats__grid">
            <article>
              <span className="icon"><IconShield /></span>
              <h3>Discipline First</h3>
              <p>Structured training for responsible and confident leadership.</p>
            </article>
            <article>
              <span className="icon"><IconFlag /></span>
              <h3>Nation Building</h3>
              <p>Cadet participation in social service and national initiatives.</p>
            </article>
            <article>
              <span className="icon"><IconStar /></span>
              <h3>All-Round Growth</h3>
              <p>Adventure, character, teamwork, and values-driven development.</p>
            </article>
          </div>
        </div>
      </section>

      {/* ---- About ---- */}
      <section className="ncx-sec">
        <div className="container">
          <Rise>
            <div className="ncx-about">
              <div className="ncx-about__text">
                <span className="eyebrow-ed">{about.eyebrow}</span>
                <h2 className="heading-ed">{about.heading}</h2>
                {about.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                <div className="ncx-motto">
                  <p className="ncx-motto__label">{about.motto.label}</p>
                  <p className="ncx-motto__text">“{about.motto.text}”</p>
                  <p className="ncx-motto__date">{about.motto.date}</p>
                </div>
              </div>
              <div className="ncx-about__media">
                <div className="ncx-about__media-inner">
                  <img src={about.image} alt="NCC parade" loading="lazy" />
                </div>
                <div className="ncx-about__media-accent" aria-hidden="true" />
              </div>
            </div>
          </Rise>
        </div>
      </section>

      {/* ---- Officer ---- */}
      <section className="ncx-sec ncx-sec--alt" id="ncc-officer">
        <div className="container">
          <Rise>
            <div className="ncx-head ncx-head--center">
              <span className="eyebrow-ed">{head.eyebrow}</span>
              <h2 className="heading-ed">{head.heading}</h2>
            </div>
          </Rise>
          <Rise>
            <div className="ncx-officer">
              <div className="ncx-officer__media">
                <img src={head.image} alt={head.name} loading="lazy" />
                <div className="ncx-officer__badge">
                  <p className="ncx-officer__badge-label">Service ID</p>
                  <p className="ncx-officer__badge-id">{head.serviceId}</p>
                </div>
              </div>
              <div className="ncx-officer__body">
                <span className="ncx-officer__role">{head.role}</span>
                <h3 className="ncx-officer__name">{head.name}</h3>
                <p className="ncx-officer__rank">{head.rank}</p>
                <div className="ncx-officer__divider" />
                <p className="ncx-officer__bio">{head.bio}</p>
              </div>
            </div>
          </Rise>
        </div>
      </section>

      {/* ---- Documents ---- */}
      <section className="ncx-sec" id="ncc-docs">
        <div className="container">
          <Rise>
            <div className="ncx-head ncx-head--center">
              <span className="eyebrow-ed">{documents.eyebrow}</span>
              <h2 className="heading-ed">{documents.heading}</h2>
              <p className="ncx-head__intro">{documents.intro}</p>
            </div>
          </Rise>
          <motion.div className="ncx-docs" variants={containerV} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}>
            {docsShown.map((doc, i) => (
              <motion.a key={i} className="ncx-doc" href={doc.url} target="_blank" rel="noopener noreferrer" variants={itemV}>
                <span className="ncx-doc__icon"><IconDoc /></span>
                <h3 className="ncx-doc__title">{doc.title}</h3>
                <span className="ncx-doc__arrow"><IconArrow /></span>
              </motion.a>
            ))}
          </motion.div>
          {isMobile && documents.items.length > DOCS_INITIAL && (
            <MoreToggle open={showAll} onClick={() => setShowAll((v) => !v)} moreLabel={`View all ${documents.items.length} documents`} />
          )}
        </div>
      </section>

      {/* ---- Quote ---- */}
      <section className="ncx-quote ncx-cta">
        <div className="container">
          <Rise>
            <div className="ncx-quote__box">
              <span className="ncx-quote__mark" aria-hidden="true">“</span>
              <h2 className="ncx-quote__title">{quote.title}</h2>
              <p className="ncx-quote__text">{quote.text}</p>
              <div className="ncx-quote__actions">
                <Link to="/contact-us" className="hero-btn hero-btn--primary">Contact NCC Team</Link>
              </div>
            </div>
          </Rise>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NCC;
